import { Feature } from 'ol'
import { Geometry, Point } from 'ol/geom'
import { Vector as VectorSource } from 'ol/source'
import { Vector as VectorLayer } from 'ol/layer'
import { Style, Icon } from 'ol/style'
import {
    PlanEvents,
    VesselPlanningModel,
} from '../models/vessel-planning-model'
import { formatTime } from './utils'

const _PROJECTION = (<any>window)['appenv'].projection

export class PolarMarkerLayer {
    private _polarMarkerPosition: [number, number] | null = null
    private _polarMarkerVisible: boolean = false
    private _nearestEta: string | null = null
    private _tooltip: HTMLElement | null = null
    private _map: any = null

    constructor() {
        this._handlePolarMarkerToggle = this._handlePolarMarkerToggle.bind(this)
        this._plotPolarMarker = this._plotPolarMarker.bind(this)
    }

    get layer() {
        return this._layer
    }

    assignMap(map: any): void {
        this._map = map
        if (map && this._tooltip) {
            this._setupTooltipInteractions()
        }
    }

    async initialize() {
        this._polarVectorSource = new VectorSource({})
        this._layer = new VectorLayer({
            source: this._polarVectorSource,
            visible: true,
            zIndex: 5, // High z-index to ensure it appears above other markers
        })

        VesselPlanningModel?.addEventListener(
            PlanEvents.TOGGLE_POLAR_PLOT_MARKER,
            <EventListener>(
                ((e: CustomEvent) => this._handlePolarMarkerToggle(e))
            ),
        )
    }

    dispose(): void {
        VesselPlanningModel?.removeEventListener(
            PlanEvents.TOGGLE_POLAR_PLOT_MARKER,
            <EventListener>(
                ((e: CustomEvent) => this._handlePolarMarkerToggle(e))
            ),
        )
    }

    private _handlePolarMarkerToggle(event: CustomEvent): void {
        const { showMarker, position, nearestEta } = event.detail

        this._polarMarkerVisible = showMarker
        this._polarMarkerPosition = showMarker ? position : null
        this._nearestEta = nearestEta || null

        if (showMarker) {
            if (!this._tooltip) {
                this._createTooltip()
                if (this._map) {
                    this._setupTooltipInteractions()
                }
            }
        } else {
            this._removeTooltip()
        }

        this._plotPolarMarker()
    }

    private _plotPolarMarker(): void {
        this._polarVectorSource!.clear()

        if (!this._polarMarkerVisible || !this._polarMarkerPosition) {
            return
        }

        const point = new Point(this._polarMarkerPosition)

        point.transform('EPSG:4326', _PROJECTION)

        const markerFeature = new Feature(point)
        markerFeature.setStyle(this._createPolarMarkerStyle())
        markerFeature.set('layer', 'polarMarkerLayer')
        markerFeature.set('type', 'PolarMarker')

        this._polarVectorSource!.addFeature(markerFeature)
    }

    private _createTooltip(): void {
        this._tooltip = document.createElement('div')
        this._tooltip.id = 'polar-tooltip-main'
        this._tooltip.style.cssText = `
            position: absolute;
            background: white;
            color: #545454;
            padding: 12px 16px;
            border-radius: 4px;
            font-size: 14px;
            font-family: MaerskText;
            pointer-events: none;
            z-index: 10000;
            display: none;
            white-space: nowrap;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            border: 1px solid #DBDBDB;
        `

        const contentContainer = document.createElement('div')
        contentContainer.id = 'polar-tooltip-content'

        const arrowContainer = document.createElement('div')
        arrowContainer.id = 'polar-tooltip-arrow-container'
        arrowContainer.style.cssText = `
            position: absolute;
            right: -9px;
            top: 50%;
            transform: translateY(-50%);
            width: 9px;
            height: 18px;
        `

        const arrowBorder = document.createElement('div')
        arrowBorder.id = 'polar-tooltip-arrow-border'
        arrowBorder.style.cssText = `
            position: absolute;
            right: 0;
            top: 0;
            width: 0;
            height: 0;
            border-top: 9px solid transparent;
            border-bottom: 9px solid transparent;
            border-left: 9px solid #e0e0e0;
        `

        // Add the main arrow
        const arrow = document.createElement('div')
        arrow.id = 'polar-tooltip-arrow-main'
        arrow.style.cssText = `
            position: absolute;
            right: 1px;
            top: 1px;
            width: 0;
            height: 0;
            border-top: 8px solid transparent;
            border-bottom: 8px solid transparent;
            border-left: 8px solid white;
        `

        arrowContainer.appendChild(arrowBorder)
        arrowContainer.appendChild(arrow)

        this._tooltip.appendChild(contentContainer)
        this._tooltip.appendChild(arrowContainer)
        document.body.appendChild(this._tooltip)
    }

    private _removeTooltip(): void {
        if (this._tooltip) {
            document.body.removeChild(this._tooltip)
            this._tooltip = null
        }
    }

    private _setupTooltipInteractions(): void {
        if (!this._map) return

        this._map.on('pointermove', (event: any) => {
            const feature = this._map.forEachFeatureAtPixel(
                event.pixel,
                (feature: any) => {
                    if (
                        feature.get('type') === 'PolarMarker' &&
                        feature.get('layer') === 'polarMarkerLayer'
                    ) {
                        return feature
                    }
                    return null
                },
            )

            if (feature && this._tooltip) {
                const eta = this._nearestEta
                    ? formatTime(new Date(this._nearestEta).toISOString())
                    : '---'

                const contentContainer = this._tooltip.querySelector(
                    '#polar-tooltip-content',
                )
                if (contentContainer) {
                    contentContainer.innerHTML = `
                            <div style="margin-bottom: 4px;">Polar Chart Advice</div>
                            <div>${eta}</div>
                        `
                }

                this._tooltip.style.display = 'block'
                this._tooltip.style.left =
                    event.pixel[0] - this._tooltip.offsetWidth - 25 + 'px'
                this._tooltip.style.top =
                    event.pixel[1] + this._tooltip.offsetHeight / 4 + 'px'
            } else if (this._tooltip) {
                this._tooltip.style.display = 'none'
            }
        })
    }

    private _createPolarMarkerStyle(): Style {
        const svgContent = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="10" fill="#058EC6"/>
        <circle cx="7" cy="7" r="7" transform="matrix(1 0 0 -1 3 17)" fill="#AE1010" stroke="#FDFFFE"/>
        <path d="M11.5535 6.18859L11.4163 6.13357V6.28141V7.98995V8.03726L11.4529 8.06727C11.5422 8.14055 11.6275 8.21788 11.7086 8.29927C11.7898 8.38065 11.867 8.46612 11.9401 8.55569L11.9701 8.59246H12.0175H13.7218H13.8694L13.8147 8.4554C13.6091 7.94025 13.3092 7.48496 12.9154 7.09018C12.5216 6.6954 12.0674 6.39468 11.5535 6.18859ZM8.60877 6.28141V6.13567L8.4728 6.1881C7.69333 6.48865 7.06915 6.98756 6.60243 7.68297C6.13589 8.3781 5.90251 9.15132 5.90251 10C5.90251 10.8487 6.13589 11.6219 6.60243 12.317C7.06888 13.012 7.69246 13.519 8.47103 13.8363L8.60877 13.8925V13.7437V12.0101V11.9606L8.56954 11.9306C8.26372 11.6967 8.02716 11.4131 7.8587 11.0793C7.69054 10.7462 7.60627 10.3869 7.60627 10C7.60627 9.61312 7.69054 9.25382 7.8587 8.92069C8.02716 8.58694 8.26372 8.30335 8.56954 8.06937L8.60877 8.03935V7.98995V6.28141ZM12.0175 11.4075H11.9665L11.9366 11.4488C11.8735 11.5358 11.8003 11.6192 11.7167 11.699C11.6307 11.7811 11.5428 11.859 11.4529 11.9327L11.4163 11.9627V12.0101V13.7186V13.8664L11.5535 13.8114C12.0674 13.6053 12.5216 13.3046 12.9154 12.9098C13.3092 12.515 13.6091 12.0597 13.8147 11.5446L13.8694 11.4075H13.7218H12.0175ZM10.6138 5.11139C11.7381 5.24434 12.6971 5.70983 13.4944 6.50906C14.2917 7.30838 14.7561 8.27 14.8887 9.39749H11.4858C11.4064 9.19431 11.2924 9.01394 11.1441 8.85714C10.9945 8.69906 10.8173 8.5794 10.6138 8.49838V5.11139ZM9.41128 14.8887C8.19811 14.7461 7.18411 14.2213 6.36501 13.3124C5.52126 12.3762 5.1 11.2737 5.1 10C5.1 8.7263 5.52126 7.62375 6.36501 6.68755C7.18411 5.77869 8.19811 5.25389 9.41128 5.11132V8.49922C9.12122 8.61789 8.88434 8.80857 8.70241 9.06972C8.50706 9.35013 8.40877 9.66108 8.40877 10C8.40877 10.339 8.50711 10.6463 8.70329 10.919C8.88539 11.1721 9.12207 11.3579 9.41128 11.4757V14.8887ZM11.4857 10.6025H14.8887C14.7561 11.73 14.2917 12.6916 13.4944 13.4909C12.6971 14.2902 11.7381 14.7557 10.6138 14.8886V11.4766C10.8162 11.3968 10.9927 11.282 11.1422 11.1322C11.2917 10.9823 11.4061 10.8054 11.4857 10.6025Z" fill="#FFFBFB" stroke="white" stroke-width="0.2"/>
        </svg>`

        const encoded = encodeURIComponent(svgContent)
        const dataUrl = `data:image/svg+xml,${encoded}`

        return new Style({
            image: new Icon({
                src: dataUrl,
                scale: 1.2,
                anchor: [0.5, 0.5],
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
            }),
            zIndex: 20, // has to be above the vessel icon
        })
    }

    private _layer: VectorLayer<VectorSource<Geometry>> | null = null
    private _polarVectorSource: VectorSource<Geometry> | null = null
}
